import { getJWTToken } from './auth';
import { authHeaders, authHeadersFor } from '../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type DomainEventType =
  | 'exit.confirmed.v1'
  | 'exit.issue_report_opened.v1'
  | 'feedback.link_clicked.v1'
  | 'program_evaluation.viewed.v1'
  | 'program_evaluation.list_viewed.v1'
  | 'program_evaluation.student_viewed.v1'
  | 'user.handoff.signed_in.v1'
  | 'progress_report.viewed.v1'
  | 'progress_report.printed.v1'
  | 'benchmark_monitoring.widget_opened.v1'
  | 'benchmark_monitoring.school_opened.v1'
  | 'benchmark_monitoring.teacher_opened.v1'
  | 'progress_monitoring.widget_opened.v1'
  | 'progress_monitoring.school_opened.v1'
  | 'progress_monitoring.teacher_opened.v1'
  | 'progress_monitoring.requirement_changed.v1'
  | 'progress_monitoring.turned_off.v1'
  | 'usage_data.widget_opened.v1'
  | 'usage_data.teacher_opened.v1'
  | 'growth.tile_opened.v1'
  | 'growth.grade_opened.v1'
  | 'growth.school_opened.v1'
  | 'growth.teacher_opened.v1'
  | 'growth.student_opened.v1'
  | 'growth.exported.v1'
  | 'page.loaded.v1';

const MAX_EVENT_PROPERTY_BYTES = 2048;

function truncateToBytes(value: string, maxBytes: number): string {
  const bytes = new TextEncoder().encode(value);
  if (bytes.length <= maxBytes) return value;
  let end = maxBytes;
  while (end > 0 && ((bytes[end] ?? 0) & 0xc0) === 0x80) end -= 1;
  return new TextDecoder().decode(bytes.subarray(0, end));
}

export type LogEventOptions = { asActor?: boolean };

export async function logEvent(
  type: DomainEventType,
  properties?: Record<string, string>,
  { asActor = false }: LogEventOptions = {},
): Promise<void> {
  const safeProperties = properties
    ? Object.fromEntries(
        Object.entries(properties).map(([k, v]) => [k, truncateToBytes(v, MAX_EVENT_PROPERTY_BYTES)]),
      )
    : properties;
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/events`, {
    method: 'POST',
    headers: {
      ...(asActor ? authHeadersFor(token, null) : authHeaders(token)),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type, properties: safeProperties }),
  });
  if (!response.ok) {
    throw new Error(`event logging failed: ${response.status}`);
  }
}
